import type { UserPreferences, UserPreferencesProvider } from '@atlaskit/editor-common/types';

import { fg } from '@confluence/feature-gating';
import { getApolloClient } from '@confluence/graphql';
import { confluenceLocalStorageInstance } from '@confluence/storage-manager';

import { parseConfluenceEditorSettings } from './EditorPreferences';
import { EditorPreferencesMutation } from './EditorPreferencesMutation.graphql';
import { EditorPreferencesQuery } from './EditorPreferencesQuery.graphql';
import type {
	EditorPreferencesMutation as EditorPreferencesMutationResponse,
	EditorPreferencesMutationVariables,
} from './__types__/EditorPreferencesMutation';
import type { EditorPreferencesQuery as EditorPreferencesQueryType } from './__types__/EditorPreferencesQuery';
import { persistBannerDismissalNewPinning } from './EditorControlsBannerDismissal';

enum Status {
	Complete = 'complete',
	Error = 'error',
	Pending = 'pending',
}

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const isSSR = () => Boolean(process.env.REACT_SSR);

const LOCAL_STORAGE_KEY = 'editor-controls.initial-preferences';

type EditorUserPreferencesProviderOptions = {
	getFallbackUserPreferences?: () => UserPreferences;
};

export class EditorUserPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	private getFallbackUserPreferences: (() => UserPreferences) | undefined;
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private providerStatus: Status = Status.Pending;

	public constructor(options: EditorUserPreferencesProviderOptions = {}) {
		if (fg('platform_editor_controls_toolbar_ssr_fix')) {
			this.getFallbackUserPreferences = options.getFallbackUserPreferences;
			this.loadPreferencesFromCache();
		}

		this.loadPreferencesInternal()
			.then(() => {
				this.providerStatus = Status.Complete;
			})
			.catch(() => {
				this.providerStatus = Status.Error;
			});
	}

	// synchronously load preferences from cache
	private loadPreferencesFromCache(): UserPreferences | undefined {
		if (!isSSR()) {
			const userPreferenceStorage = confluenceLocalStorageInstance.getItem(LOCAL_STORAGE_KEY);
			if (userPreferenceStorage) {
				this.userPreferences = JSON.parse(userPreferenceStorage);
			}
		}

		if (!this.apolloClient) {
			return undefined;
		}

		try {
			const result = this.apolloClient.readQuery<EditorPreferencesQueryType>({
				query: EditorPreferencesQuery,
			});

			if (result?.userPreferences) {
				this.userPreferences = parseConfluenceEditorSettings(
					result.userPreferences?.confluenceEditorSettings,
				);

				// if we have a cached value, we can set the status to complete
				// since we are not going to make a network request
				// to fetch the preferences with loadPreferencesInternal and not skipCache
				if (this.providerStatus !== Status.Complete) {
					this.providerStatus = Status.Complete;
				}
			}

			return this.userPreferences;
		} catch (error) {
			return this.userPreferences;
		}
	}

	private async loadPreferencesInternal(options?: {
		skipCache?: boolean;
	}): Promise<UserPreferences> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const { data } = await this.apolloClient.query<EditorPreferencesQueryType>({
			query: EditorPreferencesQuery,
			fetchPolicy: options?.skipCache ? 'no-cache' : 'cache-first',
		});

		this.userPreferences = parseConfluenceEditorSettings(
			data?.userPreferences?.confluenceEditorSettings,
		);

		this.updateLocalStorage();

		return this.userPreferences;
	}

	async loadPreferences(): Promise<UserPreferences> {
		try {
			const userPreferences = await this.loadPreferencesInternal({ skipCache: true });
			// can recover from the status previous error
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Complete;
			}
			return userPreferences;
		} catch (error) {
			// We will ignore subssequent errors if thte status was complete
			// And will use the data from previous request
			if (this.providerStatus !== Status.Complete) {
				this.providerStatus = Status.Error;
			}
			return Promise.reject(error);
		}
	}

	private updateLocalStorage(): void {
		if (!isSSR() && fg('platform_editor_controls_toolbar_ssr_fix')) {
			confluenceLocalStorageInstance.setItem(
				LOCAL_STORAGE_KEY,
				JSON.stringify(this.userPreferences),
			);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		const variables = {
			[key as keyof EditorPreferencesMutationVariables]: value,
		} as EditorPreferencesMutationVariables;

		this.userPreferences[key] = value;

		const { data } = await this.apolloClient.mutate<
			EditorPreferencesMutationResponse,
			EditorPreferencesMutationVariables
		>({
			mutation: EditorPreferencesMutation,
			variables,
			update: (cache) => {
				// this is required to indicate toolbar changes to any useQuery for EditorPreferencesQuery, particularly progressive object header
				const getValue = () => {
					if (fg('platform_editor_controls_default_docking')) {
						return value ?? null;
					}
					return value === 'top' ? value : null; // Null is required since apollo cache uses shallow reference equality
				};

				cache.writeQuery({
					query: EditorPreferencesQuery,
					data: {
						userPreferences: {
							__typename: 'UserPreferences',
							confluenceEditorSettings: {
								__typename: 'ConfluenceEditorSettings',
								toolbarDockingInitialPosition: getValue(),
							},
						},
					},
				});
			},
		});

		const updatedUserPreferences = parseConfluenceEditorSettings(
			data?.updateUserPreferences?.confluenceEditorSettings,
		);

		this.userPreferences = {
			...this.userPreferences,
			...updatedUserPreferences,
		};

		if (fg('platform_update_onboarding_banner_fully_dimissed')) {
			if (value === 'top') {
				persistBannerDismissalNewPinning('toolbar-inline');
			} else if (value === 'none') {
				persistBannerDismissalNewPinning('toolbar-top');
			}
		}
		this.updateLocalStorage();
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		if (fg('platform_editor_controls_toolbar_ssr_fix')) {
			const fallbackUserPreferences = this.getFallbackUserPreferences?.() || {};
			return this.userPreferences[key] || fallbackUserPreferences[key];
		}

		if (this.providerStatus !== Status.Complete) {
			return undefined;
		}
		return this.userPreferences[key] || null;
	}
}
